import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Table, Badge, Alert, Modal, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch, 
  faEdit, 
  faTrash, 
  faEye, 
  faFileContract,
  faUser,
  faHome,
  faCalendarAlt,
  faMoneyBillWave,
  faFilter,
  faSignOutAlt,
  faDownload,
  faSync
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { contractService } from '../../services/contractService';
import { tenantService } from '../../services/tenantService';
import { propertyService } from '../../services/propertyService';
import { formatCurrency } from '../../utils/currencyFormatter';

const ContractsList = () => {
  const navigate = useNavigate();
  const { userCompany, logout } = useAuth();
  
  const [contracts, setContracts] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [userCompany]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [contractsData, tenantsData, unitsData] = await Promise.all([
        contractService.getContractsByCompany(userCompany),
        tenantService.getTenantsByCompany(userCompany),
        propertyService.getUnitsByCompany(userCompany)
      ]);
      
      setContracts(contractsData);
      setTenants(tenantsData);
      setPropertyUnits(unitsData);
    } catch (error) {
      console.error('Error fetching contracts data:', error);
      setError('Failed to load contracts: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleDelete = async () => {
    if (!contractToDelete) return;
    
    try {
      setDeleting(true);
      await contractService.deleteContract(contractToDelete.id);
      
      // Update property unit status to available
      if (contractToDelete.propertyUnitId) {
        await propertyService.updatePropertyUnit(contractToDelete.propertyUnitId, {
          status: 'available'
        });
      }
      
      setContracts(contracts.filter(c => c.id !== contractToDelete.id));
      setShowDeleteModal(false);
      setContractToDelete(null);
    } catch (error) {
      console.error('Error deleting contract:', error);
      setError('Failed to delete contract: ' + (error.message || error));
    } finally {
      setDeleting(false);
    }
  };

  const confirmDelete = (contract) => {
    setContractToDelete(contract);
    setShowDeleteModal(true);
  };

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Unknown Tenant';
  };

  const getPropertyUnitInfo = (unitId) => {
    const unit = propertyUnits.find(u => u.id === unitId);
    return unit ? `${unit.unitNumber} (${unit.unitType})` : 'Unknown Unit';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: 'success', text: 'Active' },
      expired: { variant: 'danger', text: 'Expired' },
      terminated: { variant: 'secondary', text: 'Terminated' },
      pending: { variant: 'warning', text: 'Pending' }
    };
    
    const config = statusConfig[status] || { variant: 'secondary', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = 
      getTenantName(contract.tenantId).toLowerCase().includes(searchTerm.toLowerCase()) ||
      getPropertyUnitInfo(contract.propertyUnitId).toLowerCase().includes(searchTerm.toLowerCase()) ||
              (contract.contractId || contract.id).toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const exportContracts = () => {
    const csvContent = [
      ['Contract ID', 'Tenant', 'Property Unit', 'Start Date', 'End Date', 'Monthly Rent', 'Total Amount', 'Status'],
      ...filteredContracts.map(contract => [
        contract.contractId || contract.id,
        getTenantName(contract.tenantId),
        getPropertyUnitInfo(contract.propertyUnitId),
        contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : 'N/A',
        contract.contractEndDate ? new Date(contract.contractEndDate).toLocaleDateString() : 'N/A',
        formatCurrency(contract.chequeAmount || 0, contract.currency || 'USD'),
        formatCurrency(contract.totalRentalAmount || 0, contract.currency || 'USD'),
        contract.status
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contracts_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Contracts</h2>
          <p className="text-muted mb-0">Manage all property contracts</p>
        </div>
        <div className="d-flex gap-2">
          <Button variant="primary" onClick={() => navigate('/manager/contracts/create')}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Create Contract
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faFileContract} className="text-primary mb-2" size="2x" />
              <h4 className="mb-1">{contracts.length}</h4>
              <p className="text-muted mb-0">Total Contracts</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faUser} className="text-success mb-2" size="2x" />
              <h4 className="mb-1">
                {contracts.filter(c => c.status === 'active').length}
              </h4>
              <p className="text-muted mb-0">Active Contracts</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faMoneyBillWave} className="text-warning mb-2" size="2x" />
              <h4 className="mb-1">
                {formatCurrency(
                  contracts.reduce((sum, c) => sum + (c.chequeAmount || 0), 0),
                  contracts[0]?.currency || 'USD'
                )}
              </h4>
              <p className="text-muted mb-0">Total Monthly Rent</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faHome} className="text-info mb-2" size="2x" />
              <h4 className="mb-1">
                {new Set(contracts.map(c => c.propertyUnitId)).size}
              </h4>
              <p className="text-muted mb-0">Occupied Units</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters and Search */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by tenant, property unit, or contract ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="terminated">Terminated</option>
                <option value="pending">Pending</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Button variant="outline-secondary" onClick={fetchData}>
                <FontAwesomeIcon icon={faSync} className="me-2" />
                Refresh
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Contracts Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              Contracts ({filteredContracts.length})
            </h5>
            <Button variant="primary" size="sm" onClick={() => navigate('/manager/contracts/create')}>
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              New Contract
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {filteredContracts.length === 0 ? (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faFileContract} className="text-muted mb-3" size="3x" />
              <h5>No contracts found</h5>
              <p className="text-muted">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Get started by creating your first contract.'
                }
              </p>
              <Button variant="primary" onClick={() => navigate('/manager/contracts/create')}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create First Contract
              </Button>
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Contract ID</th>
                  <th>Tenant</th>
                  <th>Property Unit</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Monthly Rent</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredContracts.map((contract) => (
                                <tr key={contract.id}>
                <td>
                  <strong className="text-primary">{contract.contractId || contract.id}</strong>
                </td>
                    <td>
                      <div>
                        <strong>{getTenantName(contract.tenantId)}</strong>
                        <div className="text-muted small">
                          <FontAwesomeIcon icon={faUser} className="me-1" />
                          {contract.tenantId}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{getPropertyUnitInfo(contract.propertyUnitId)}</strong>
                        <div className="text-muted small">
                          <FontAwesomeIcon icon={faHome} className="me-1" />
                          {contract.propertyUnitId}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        {contract.contractStartDate ? new Date(contract.contractStartDate).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div>
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        {contract.contractEndDate ? new Date(contract.contractEndDate).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div>
                        <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                        {formatCurrency(contract.chequeAmount || 0, contract.currency || 'USD')}
                      </div>
                    </td>
                    <td>
                      <div>
                        <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                        {formatCurrency(contract.totalRentalAmount || 0, contract.currency || 'USD')}
                      </div>
                    </td>
                    <td>{getStatusBadge(contract.status)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => navigate(`/manager/contracts/${contract.id}`)}
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => navigate(`/manager/contracts/${contract.id}/edit`)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => confirmDelete(contract)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Contract</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete the contract <strong>"{contractToDelete?.id}"</strong>?</p>
          <p className="text-muted">
            Tenant: {contractToDelete ? getTenantName(contractToDelete.tenantId) : ''}<br />
            Property: {contractToDelete ? getPropertyUnitInfo(contractToDelete.propertyUnitId) : ''}
          </p>
          <p className="text-danger">
            <strong>Warning:</strong> This action cannot be undone. The property unit will be marked as available.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete Contract'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContractsList; 